/**
 * Detalle de un comité: datos, admins, métricas, acciones (login-as,
 * suspender/reactivar).
 */
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Building2,
  CheckCircle2,
  ExternalLink,
  KeyRound,
  Pause,
  Play,
  ShieldCheck,
  Users,
  XCircle,
} from "lucide-react";

import { useToast } from "../../components/Toast";
import { Badge, Card, Cargando, ErrorCarga, PageHeader } from "../../components/ui";
import { ApiError } from "../../lib/api";
import { authStateFromToken } from "../../lib/auth";
import { useAuth } from "../auth/useAuth";
import {
  detalleEmpresa,
  loginAsEmpresa,
  reactivarEmpresa,
  suspenderEmpresa,
} from "./api";
import { AsistenteEmpresaCard } from "./AsistenteEmpresaCard";
import { DteEmpresaCard } from "./DteEmpresaCard";
import { ModulosEmpresaCard } from "./ModulosEmpresaCard";

export function EmpresaDetailPage(): JSX.Element {
  const { empresaId } = useParams<{ empresaId: string }>();
  const id = Number(empresaId);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast();
  const { login: replaceAuth } = useAuth();
  const [motivo, setMotivo] = useState("");

  const detalle = useQuery({
    queryKey: ["superadmin", "empresa", id],
    queryFn: () => detalleEmpresa(id),
    enabled: Number.isFinite(id),
  });

  const suspendMut = useMutation({
    mutationFn: () => suspenderEmpresa(id),
    onSuccess: () => {
      toast.success("Comité suspendido");
      queryClient.invalidateQueries({ queryKey: ["superadmin"] });
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : "No se pudo suspender"),
  });

  const reactivateMut = useMutation({
    mutationFn: () => reactivarEmpresa(id),
    onSuccess: () => {
      toast.success("Comité reactivado");
      queryClient.invalidateQueries({ queryKey: ["superadmin"] });
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : "No se pudo reactivar"),
  });

  const loginAsMut = useMutation({
    mutationFn: () => loginAsEmpresa(id, motivo.trim()),
    onSuccess: (data) => {
      // Reemplazamos el token en localStorage. El nuevo token apunta a
      // la empresa target con is_superadmin=True preservado, por lo que
      // el operador puede volver a la Console cuando termine.
      const next = authStateFromToken(data.access_token);
      replaceAuth(data.access_token);
      toast.success(`Entrando como admin del comité #${next.empresaId}`);
      navigate("/socios");
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : "No se pudo hacer login-as"),
  });

  if (!Number.isFinite(id)) {
    return <ErrorCarga mensaje="ID de empresa inválido" />;
  }
  if (detalle.isLoading) return <Cargando />;
  if (detalle.isError)
    return <ErrorCarga error={detalle.error} onReintentar={() => detalle.refetch()} />;
  if (!detalle.data) return <div className="text-sm text-neutral-600">Sin datos</div>;

  const { empresa, admins, metricas } = detalle.data;
  const ult = metricas.ultima_lectura_at
    ? new Date(metricas.ultima_lectura_at).toLocaleString("es-CL")
    : "—";

  return (
    <div className="space-y-5 max-w-4xl">
      <PageHeader
        title={empresa.nombre}
        icon={Building2}
        volver={{ etiqueta: "Comités", ruta: "/superadmin/empresas" }}
        subtitle={`${empresa.rut} · ${empresa.email}`}
        actions={
          <>
            {empresa.is_active ? (
              <Badge tone="emerald">
                <CheckCircle2 className="h-3.5 w-3.5" strokeWidth={1.5} /> Activa
              </Badge>
            ) : (
              <Badge tone="rose">
                <XCircle className="h-3.5 w-3.5" strokeWidth={1.5} /> Suspendida
              </Badge>
            )}
            {empresa.is_active ? (
              <button
                type="button"
                onClick={() => suspendMut.mutate()}
                disabled={suspendMut.isPending}
                className="inline-flex items-center gap-1 rounded-md bg-amber-50 px-3 py-1.5 text-sm font-medium text-amber-800 hover:bg-amber-100 disabled:opacity-50"
              >
                <Pause className="h-4 w-4" strokeWidth={1.5} />
                Suspender
              </button>
            ) : (
              <button
                type="button"
                onClick={() => reactivateMut.mutate()}
                disabled={reactivateMut.isPending}
                className="inline-flex items-center gap-1 rounded-md bg-emerald-50 px-3 py-1.5 text-sm font-medium text-emerald-800 hover:bg-emerald-100 disabled:opacity-50"
              >
                <Play className="h-4 w-4" strokeWidth={1.5} />
                Reactivar
              </button>
            )}
          </>
        }
      />

      {/* Métricas */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Socios activos" value={metricas.total_socios_activos} />
        <Stat label="Contratos vigentes" value={metricas.total_contratos_vigentes} />
        <Stat label="Lecturas mes actual" value={metricas.lecturas_mes_actual} />
        <Stat
          label="Cierres pendientes"
          value={metricas.cierres_pendientes}
          tone={metricas.cierres_pendientes > 0 ? "warning" : "neutral"}
        />
      </div>
      <div className="text-xs text-neutral-500">Última lectura registrada: {ult}</div>

      {/* Datos */}
      <Card>
        <h2 className="text-sm font-semibold text-neutral-900 mb-3">Datos del comité</h2>
        <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
          <Row label="Dirección" value={empresa.direccion ?? "—"} />
          <Row label="Teléfono" value={empresa.telefono ?? "—"} />
          <Row label="Giro" value={empresa.giro ?? "—"} />
          <Row label="Creado" value={new Date(empresa.created_at).toLocaleDateString("es-CL")} />
        </dl>
      </Card>

      <ModulosEmpresaCard empresa={empresa} />
      <AsistenteEmpresaCard empresaId={empresa.id} />
      <DteEmpresaCard empresaId={empresa.id} />

      {/* Usuarios */}
      <Card>
        <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
          <h2 className="text-sm font-semibold text-neutral-900 flex items-center gap-2">
            <Users className="h-4 w-4 text-brand-600" strokeWidth={1.5} />
            Usuarios del comité ({admins.length})
          </h2>
          <div className="flex items-center gap-2">
            <Link
              to={`/superadmin/roles?empresa=${empresa.id}`}
              className="inline-flex items-center gap-1 rounded-md bg-amber-50 px-3 py-1 text-xs font-medium text-amber-800 hover:bg-amber-100"
            >
              <KeyRound className="h-3.5 w-3.5" strokeWidth={1.5} />
              Roles del comité
            </Link>
            <Link
              to={`/superadmin/empresas/${empresa.id}/usuarios`}
              className="inline-flex items-center gap-1 rounded-md bg-brand-50 px-3 py-1 text-xs font-medium text-brand-700 hover:bg-brand-100"
            >
              <Users className="h-3.5 w-3.5" strokeWidth={1.5} />
              Gestionar usuarios
            </Link>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-neutral-500 text-left">
              <tr>
                <th scope="col" className="pb-2">Nombre</th>
                <th scope="col" className="pb-2">Email</th>
                <th scope="col" className="pb-2">Rol</th>
                <th scope="col" className="pb-2">Cargo</th>
                <th scope="col" className="pb-2">Estado</th>
              </tr>
            </thead>
            <tbody>
              {admins.map((u) => (
                <tr key={u.id} className="border-t border-neutral-100">
                  <td className="py-2">{u.nombre}</td>
                  <td className="py-2 text-neutral-600">{u.email}</td>
                  <td className="py-2 capitalize">{u.role}</td>
                  <td className="py-2 text-neutral-700">{u.cargo ?? "—"}</td>
                  <td className="py-2">
                    {u.is_active ? (
                      <Badge tone="emerald">Activo</Badge>
                    ) : (
                      <Badge tone="slate">Inactivo</Badge>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Login-as */}
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
        <h2 className="text-sm font-semibold text-amber-900 flex items-center gap-2">
          <ShieldCheck className="h-4 w-4" />
          Login como admin de este comité (soporte)
        </h2>
        <p className="text-xs text-amber-800 mt-1">
          Cada login-as queda en log estructurado con tu user_id, la empresa
          visitada y el motivo. El JWT actual se reemplaza — para volver a la
          Console, vas a tu menú lateral y entras de nuevo a Comités.
        </p>
        <div className="mt-3 flex items-end gap-3 flex-wrap">
          <label className="block text-sm flex-1 min-w-[260px]">
            <span className="block font-medium text-amber-900 mb-1">Motivo</span>
            <input
              type="text"
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder="Ticket #42 — investigar lectura duplicada"
              className="block w-full rounded-md border border-amber-300 bg-white px-3 py-2 text-sm"
            />
          </label>
          <button
            type="button"
            onClick={() => loginAsMut.mutate()}
            disabled={
              loginAsMut.isPending || motivo.trim().length < 3 || !empresa.is_active
            }
            className="inline-flex items-center gap-2 rounded-md bg-amber-700 px-4 py-2 text-sm font-medium text-white hover:bg-amber-800 disabled:opacity-50"
            title={
              empresa.is_active
                ? "Entrar como admin"
                : "Reactiva el comité antes de entrar"
            }
          >
            <ExternalLink className="h-4 w-4" strokeWidth={1.5} />
            Entrar
          </button>
        </div>
      </div>
    </div>
  );
}

function Stat({
  label,
  value,
  tone = "neutral",
}: {
  label: string;
  value: number;
  tone?: "neutral" | "warning";
}): JSX.Element {
  return (
    <Card padding="sm">
      <div className="text-xs text-neutral-500">{label}</div>
      <div
        className={
          tone === "warning"
            ? "text-2xl font-bold text-amber-700"
            : "text-2xl font-bold text-neutral-900"
        }
      >
        {value}
      </div>
    </Card>
  );
}

function Row({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <>
      <dt className="text-neutral-500">{label}</dt>
      <dd className="text-neutral-900">{value}</dd>
    </>
  );
}
